import { loadQueue, removeOperation, updateOperationStatus } from './offlineQueue';
import type { OfflineOperation } from '@/types/offlineQueue';
import { isRemoteSyncEnabled, FEATURES } from '@/config/features';

// Sync engine plan:
// - When online AND remote sync is enabled, replay queued operations against the backend.
// - Mark operations as syncing/failed/done so the UI can reflect pending work.
// - If remote sync is disabled (default), operations are simply marked as done without network calls.

export type SyncResult = {
  status: 'success' | 'offline' | 'error' | 'disabled';
  synced: number;
  failed: number;
  pending: number;
  errors: string[];
};

const API_BASE = FEATURES.API_BASE_URL;

const buildTripUrl = (id?: string) => {
  const base = API_BASE?.replace(/\/$/, '') || '';
  if (!id) {
    return `${base}/api/trips`;
  }
  return `${base}/api/trips/${encodeURIComponent(id)}`;
};

const replayOperation = async (op: OfflineOperation, fetcher: typeof fetch): Promise<Response> => {
  if (op.entityType !== 'trip') {
    return new Response(null, { status: 204 });
  }
  const headers = { 'Content-Type': 'application/json' };
  if (op.type === 'create') {
    return fetcher(buildTripUrl(), {
      method: 'POST',
      headers,
      body: JSON.stringify(op.payload ?? {}),
    });
  }
  if (op.type === 'update') {
    return fetcher(buildTripUrl(op.entityId), {
      method: 'PUT',
      headers,
      body: JSON.stringify(op.payload ?? {}),
    });
  }
  // delete
  return fetcher(buildTripUrl(op.entityId), {
    method: 'DELETE',
    headers,
    body: JSON.stringify(op.payload ?? {}),
  });
};

export async function syncOfflineQueue(fetcher?: typeof fetch): Promise<SyncResult> {
  // If remote sync is not enabled, mark all pending operations as done (local-only mode)
  if (!isRemoteSyncEnabled()) {
    const queue = await loadQueue();
    const pending = queue.filter((op) => op.status === 'pending' || op.status === 'failed');

    // Mark all pending/failed operations as done (they're already applied locally)
    for (const op of pending) {
      await updateOperationStatus(op.id, 'done');
    }

    return {
      status: 'disabled',
      synced: pending.length,
      failed: 0,
      pending: 0,
      errors: [],
    };
  }

  const effectiveFetch = fetcher ?? (typeof fetch !== 'undefined' ? fetch : undefined);

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { status: 'offline', synced: 0, failed: 0, pending: 0, errors: ['offline'] };
  }

  if (!effectiveFetch) {
    return { status: 'error', synced: 0, failed: 0, pending: 0, errors: ['fetch-unavailable'] };
  }

  const queue = await loadQueue();
  if (!queue.length) {
    return { status: 'success', synced: 0, failed: 0, pending: 0, errors: [] };
  }

  let synced = 0;
  let failed = 0;
  const errors: string[] = [];

  for (const op of queue) {
    try {
      await updateOperationStatus(op.id, 'syncing', { incrementAttempt: true });
      const res = await replayOperation(op, effectiveFetch);
      if (res.ok) {
        synced += 1;
        await removeOperation(op.id);
        continue;
      }
      failed += 1;
      const isConflict = res.status === 409 || res.status === 422;
      const message = isConflict ? `conflict (${res.status})` : `http-${res.status}`;
      errors.push(message);
      await updateOperationStatus(op.id, 'failed', { error: message });
    } catch (e: any) {
      failed += 1;
      const message = typeof e?.message === 'string' ? e.message : 'network-error';
      errors.push(message);
      await updateOperationStatus(op.id, 'failed', { error: message });
    }
  }

  const remaining = await loadQueue();
  const pending = remaining.filter((op) => op.status !== 'done').length;
  const status: SyncResult['status'] = failed > 0 ? 'error' : 'success';
  return { status, synced, failed, pending, errors };
}
